import * as React from 'react';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/utils';

export interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

interface DateRangePickerProps {
  value?: DateRange;
  onChange?: (range: DateRange) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  align?: 'left' | 'right';
}

const startOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const formatDate = (date: Date) =>
  date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });

const toInputDate = (date?: Date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseLocalDate = (value: string): Date | undefined => {
  const parts = value.split('-');

  if (parts.length !== 3) return undefined;

  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);

  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
    return undefined;
  }

  return new Date(year, month - 1, day);
};
/* ---------- Presets ---------- */

const PRESET_RANGES = [
  {
    label: 'Today',
    value: 'today',
    getRange: () => ({
      from: startOfDay(new Date()),
      to: endOfDay(new Date()),
    }),
  },
  {
    label: 'Yesterday',
    value: 'yesterday',
    getRange: () => {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      return { from: startOfDay(d), to: endOfDay(d) };
    },
  },
  {
    label: 'Last 7 days',
    value: 'last-7',
    getRange: () => {
      const from = new Date();
      from.setDate(from.getDate() - 6);
      return { from: startOfDay(from), to: endOfDay(new Date()) };
    },
  },
  {
    label: 'Last 30 days',
    value: 'last-30',
    getRange: () => {
      const from = new Date();
      from.setDate(from.getDate() - 30);
      return { from: startOfDay(from), to: endOfDay(new Date()) };
    },
  },
  {
    label: 'Last 90 days',
    value: 'last-90',
    getRange: () => {
      const from = new Date();
      from.setDate(from.getDate() - 89);
      return { from: startOfDay(from), to: endOfDay(new Date()) };
    },
  },
  {
    label: 'This month',
    value: 'this-month',
    getRange: () => {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { from: startOfDay(from), to: endOfDay(to) };
    },
  },
];

export function DateRangePicker({
  value,
  onChange,
  placeholder = 'Select date range',
  className,
  disabled = false,
  align = 'left',
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [fromInput, setFromInput] = React.useState('');
  const [toInput, setToInput] = React.useState('');
  const [isInteractingWithInput, setIsInteractingWithInput] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setFromInput(toInputDate(value?.from));
    setToInput(toInputDate(value?.to));
  }, [value]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current) return;

      if (!containerRef.current.contains(event.target as Node) && !isInteractingWithInput) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen, isInteractingWithInput]);

  const handlePresetChange = (presetValue: string) => {
    const preset = PRESET_RANGES.find((p) => p.value === presetValue);
    if (preset && onChange) {
      onChange(preset.getRange());
    }
  };

  const handleFromChange = (dateString: string) => {
    setFromInput(dateString);
    if (!onChange) return;

    if (!dateString) {
      onChange({ from: undefined, to: value?.to });
      return;
    }

    const date = parseLocalDate(dateString);
    if (!date) return;

    onChange({ from: startOfDay(date), to: value?.to });
  };

  const handleToChange = (dateString: string) => {
    setToInput(dateString);
    if (!onChange) return;

    if (!dateString) {
      onChange({ from: value?.from, to: undefined });
      return;
    }

    const date = parseLocalDate(dateString);
    if (!date) return;

    onChange({ from: value?.from, to: endOfDay(date) });
  };

  const clearRange = () => {
    if (onChange) {
      onChange({ from: undefined, to: undefined });
    }
    setFromInput('');
    setToInput('');
  };

  const hasError = value?.from && value?.to ? value.from.getTime() > value.to.getTime() : false;

  const formatDisplayText = () => {
    if (!value?.from && !value?.to) return placeholder;
    if (value.from && value.to) return `${formatDate(value.from)} - ${formatDate(value.to)}`;
    if (value.from) return `From ${formatDate(value.from)}`;
    if (value.to) return `Until ${formatDate(value.to)}`;
    return placeholder;
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <Button
        variant="outline"
        onClick={() => setIsOpen((prev) => !prev)}
        disabled={disabled}
        className={cn(
          'w-full justify-start text-left font-normal',
          !value?.from && !value?.to && 'text-muted-foreground',
          hasError && 'border-red-500 text-red-500'
        )}
      >
        <CalendarIcon className="mr-2 h-4 w-4" />
        {formatDisplayText()}

        {(value?.from || value?.to) && (
          <X
            className="ml-auto h-4 w-4 hover:bg-muted rounded"
            onClick={(e) => {
              e.stopPropagation();
              clearRange();
            }}
          />
        )}
      </Button>

      {hasError && <p className="text-sm text-red-500 mt-1">Start date cannot be after end date</p>}

      {isOpen && (
        <Card
          className={cn(
            'absolute top-full mt-2 z-50 w-full min-w-[280px]',
            align === 'right' ? 'right-0' : 'left-0'
          )}
        >
          <CardContent className="relative p-4 space-y-4">
            <button
              type="button"
              aria-label="Close date range picker"
              onClick={() => setIsOpen(false)}
              className="absolute top-3 right-3 rounded-sm p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>

            <div>
              <Label className="text-sm font-medium">Quick Select</Label>
              <Select
                onValueChange={(val) => {
                  handlePresetChange(val);
                  setIsInteractingWithInput(false);
                }}
                onOpenChange={(open) => {
                  setIsInteractingWithInput(open);
                }}
              >
                <SelectTrigger className="w-full mt-1">
                  <SelectValue placeholder="Choose a preset" />
                </SelectTrigger>
                <SelectContent>
                  {PRESET_RANGES.map((preset) => (
                    <SelectItem key={preset.value} value={preset.value}>
                      {preset.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">From Date</Label>
                <Input
                  type="date"
                  value={fromInput}
                  onFocus={() => setIsInteractingWithInput(true)}
                  onBlur={(e) => {
                    setIsInteractingWithInput(false);
                    handleFromChange(e.target.value);
                  }}
                  onChange={(e) => {
                    setFromInput(e.target.value);
                  }}
                />
              </div>

              <div>
                <Label className="text-sm font-medium">To Date</Label>
                <Input
                  type="date"
                  value={toInput}
                  onFocus={() => setIsInteractingWithInput(true)}
                  onBlur={(e) => {
                    setIsInteractingWithInput(false);
                    handleToChange(e.target.value);
                  }}
                  onChange={(e) => {
                    setToInput(e.target.value);
                  }}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-2 border-t">
              <Button variant="outline" size="sm" onClick={clearRange}>
                Clear
              </Button>
              <Button size="sm" disabled={hasError} onClick={() => setIsOpen(false)}>
                Apply
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
